import { uuidV4 } from '../crypto/random.js';

export function makeAriaId(prefix = 'rr'): string {
  return `${prefix}-${uuidV4().slice(0, 8)}`;
}

export interface DialogAriaProps {
  role: 'dialog';
  'aria-modal': true;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
}

export interface ComboboxAriaProps {
  role: 'combobox';
  'aria-expanded': boolean;
  'aria-controls': string;
  'aria-haspopup': 'listbox';
  'aria-autocomplete': 'list' | 'inline' | 'both' | 'none';
}

export interface ListboxAriaProps {
  role: 'listbox';
  id: string;
  'aria-multiselectable'?: boolean;
}

export interface MenuAriaProps {
  role: 'menu';
  'aria-labelledby'?: string;
  'aria-orientation'?: 'horizontal' | 'vertical';
}

export interface TabListAriaProps {
  role: 'tablist';
  'aria-orientation'?: 'horizontal' | 'vertical';
}

export interface DisclosureAriaProps {
  'aria-expanded': boolean;
  'aria-controls': string;
}

export const ariaProps = {
  dialog(opts: { labelledBy?: string; describedBy?: string } = {}): DialogAriaProps {
    return {
      role: 'dialog',
      'aria-modal': true,
      ...(opts.labelledBy ? { 'aria-labelledby': opts.labelledBy } : {}),
      ...(opts.describedBy ? { 'aria-describedby': opts.describedBy } : {})
    };
  },
  combobox(opts: { expanded: boolean; controls: string; autocomplete?: 'list' | 'inline' | 'both' | 'none' }): ComboboxAriaProps {
    return {
      role: 'combobox',
      'aria-expanded': opts.expanded,
      'aria-controls': opts.controls,
      'aria-haspopup': 'listbox',
      'aria-autocomplete': opts.autocomplete ?? 'list'
    };
  },
  listbox(opts: { id: string; multiselectable?: boolean }): ListboxAriaProps {
    return {
      role: 'listbox',
      id: opts.id,
      ...(opts.multiselectable !== undefined ? { 'aria-multiselectable': opts.multiselectable } : {})
    };
  },
  menu(opts: { labelledBy?: string; orientation?: 'horizontal' | 'vertical' } = {}): MenuAriaProps {
    return {
      role: 'menu',
      ...(opts.labelledBy ? { 'aria-labelledby': opts.labelledBy } : {}),
      ...(opts.orientation ? { 'aria-orientation': opts.orientation } : {})
    };
  },
  tabs(opts: { orientation?: 'horizontal' | 'vertical' } = {}): TabListAriaProps {
    return {
      role: 'tablist',
      ...(opts.orientation ? { 'aria-orientation': opts.orientation } : {})
    };
  },
  disclosure(opts: { expanded: boolean; controls: string }): DisclosureAriaProps {
    return {
      'aria-expanded': opts.expanded,
      'aria-controls': opts.controls
    };
  }
};
